import { useMatch } from 'react-router-dom';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { Stack } from '@components/stack';
import { useIsMobile } from '@hooks/use-is-mobile';
import { pxToRem } from '@utils/converters';

import { FilterButton } from 'components/FilterButton';
import { Header } from 'components/headers';
import { JobBulkActions } from 'components/job-bulk-actions';
import { JobOptions } from 'components/JobOptions';
import { FavoriteToggle } from 'components/navigation/favorites';
import { SearchBoxInput } from 'components/SearchBox';
import { SearchButton } from 'components/SearchButton';
import { Tab, TabList, useTabState } from 'components/Tabs';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useJobTabs } from 'hooks/useJobTabs';
import { useStore } from 'models/Provider';

export const JobHeader = observer(({ job, filters }) => {
  const { hasRolePermission } = usePermissions();
  if (!job) return <Header />;
  const section = useMatch('/jobs/:jobID/:section/*')?.params.section;
  const showBulk =
    (job.listView.selectedApplications || []).length > 0 &&
    hasRolePermission('applicants:use-bulk-actions');
  return (
    <Header
      selectionHeader={<JobBulkActions job={job} />}
      showSelectionHeader={showBulk}
      mobileTitle={job.detailLoaded ? job.title : ''}
      tabs={<JobTabs job={job} section={section} />}
      breadcrumbs={<JobCrumbs job={job} />}
      actions={<JobActions job={job} filters={filters} section={section} />}
    />
  );
});

const JobTabs = observer(({ job, section }) => {
  const tabs = useJobTabs(job);
  const sectionTabIndex = tabs.findIndex((item) => item.value === section);
  const tabId = sectionTabIndex > 0 ? sectionTabIndex : 0;
  const tabState = useTabState({ selectedId: tabId });

  return (
    <TabList state={tabState} label="Job selection">
      {tabs.map((tab) => (
        <Tab data-cy={'job-tabs:' + tab.value} key={tab.value} link={tab.link}>
          <div>{tab.display}</div>
        </Tab>
      ))}
    </TabList>
  );
});

const JobCrumbs = observer(({ job }) => {
  const { t } = useStore();

  let items = [{ label: t('navigation.jobs'), link: '/jobs' }];
  if (job.detailLoaded) {
    items.push({ label: job.title });
  }

  return (
    <Stack orientation="horizontal" space={2} align="center">
      <Breadcrumb items={items} />
      <FavoriteToggle contentType="job" objectId={job.id} />
    </Stack>
  );
});

const JobActions = observer(({ job, filters, section }) => {
  return (
    <>
      <Stack orientation="horizontal" space={2} justify="flex-end" style={{ flexGrow: 1 }}>
        {section === 'list' && <JobListActions job={job} filters={filters} />}
        {section === 'info' && <JobInfoActions job={job} />}
        {section === 'pipeline' && <JobPipelineActions job={job} filters={filters} />}
        {section === 'statistics' && <div id="job-statistics-actions" />}
        {section === 'promote' && <div id="job-promote-actions" />}
      </Stack>
    </>
  );
});

const AddApplicant = observer(({ job, fullWidth = false }) => {
  const { t } = useStore();
  const { hasRolePermission } = usePermissions();
  const { showModal } = useModal();
  if (!hasRolePermission('application:add')) {
    return null;
  }
  return (
    <Button
      style={{ ...(fullWidth && { width: '100%' }) }}
      onClick={() => {
        showModal('ADD_APPLICANT', {
          _container: {
            closeOnOverlayClickOrEsc: false,
          },
          job,
        });
      }}
      text={t('job.addapplicant')}
    />
  );
});

const JobListActions = observer(({ job, filters }) => {
  const { t } = useStore();
  const isMobile = useIsMobile();
  const { hasRolePermission } = usePermissions();
  if (isMobile) {
    return (
      <JobListMobileActions>
        <Stack space={2}>
          <AddApplicant job={job} />
          <SearchBoxInput
            placeholder={t('job.applications.search.tip')}
            value={job.settings.searchStr}
            onChange={job.settings.setSearchValue}
          />
          <Stack space={2} orientation="horizontal" justify="flex-end">
            <FilterButton filters={filters} />
          </Stack>
        </Stack>
      </JobListMobileActions>
    );
  }
  return (
    <>
      <SearchButton
        value={job.settings.searchStr}
        onChange={job.settings.setSearchValue}
        clear={job.settings.clearSearchValue}
        isOpen={job.settings.searchOpen}
        onClose={job.settings.closeSearch}
        onOpen={job.settings.openSearch}
        placeholder={t('job.applications.search.tip')}
      />
      <FilterButton filters={filters} />
      {hasRolePermission('job:options') && <JobOptions job={job} transparent={false} />}
      <AddApplicant job={job} />
    </>
  );
});

const JobListMobileActions = styled.div((p) => ({
  padding: pxToRem(24),
  width: '100%',
  '> *': {
    width: '100%',
  },
}));

const JobPipelineActions = observer(({ job, filters }) => {
  const { t } = useStore();
  const { hasRolePermission } = usePermissions();
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <Stack space={2} style={{ flexGrow: 1 }}>
        <AddApplicant job={job} fullWidth />
        <Stack space={2} orientation="horizontal">
          <SearchBoxInput
            placeholder={t('job.applications.search.tip')}
            value={job.settings.searchStr}
            onChange={job.settings.setSearchValue}
          />
          <FilterButton filters={filters} />
        </Stack>
      </Stack>
    );
  }
  return (
    <>
      <SearchButton
        value={job.settings.searchStr}
        onChange={job.settings.setSearchValue}
        clear={job.settings.clearSearchValue}
        isOpen={job.settings.searchOpen}
        onClose={job.settings.closeSearch}
        onOpen={job.settings.openSearch}
        placeholder={t('job.applications.search.tip')}
      />
      <FilterButton filters={filters} />
      {hasRolePermission('job:options') && <JobOptions job={job} transparent={false} />}
      <AddApplicant job={job} />
    </>
  );
});

const JobInfoActions = observer(({ job }) => {
  const { hasRolePermission } = usePermissions();
  return <>{hasRolePermission('job:options') && <JobOptions job={job} transparent={false} />}</>;
});
